// Please do not convert to a functional component unless you know what you're doing.

import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';

import {
  FormField,
  FormHelp,
  IconZillowLogo,
  Input,
  Label,
  Paragraph,
  Spacer,
  Text,
  Textarea,
} from '@zillow/constellation';
import { SelectionGroup } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import { PAYMENT_METHOD } from '../../../constants/index';
import { parseElectronicPaymentOptions } from '../../../utils/stringParsing';
import { usePreSubmitCallback } from '../../../contexts/PreSubmitCallbackContext';

type Props = {
  section: Section;
  setSectionValues(keyValuePairs: object): void;
};

const { ZILLOW_PAYMENTS } = PAYMENT_METHOD.OTHER_CONSTANTS;
const { ELECTRONIC_SERVICE_TYPE, ELECTRONIC_IS_ENABLED, DIRECT_IS_ENABLED, CHECK_IS_ENABLED, CHECK_ADDRESS } =
  PAYMENT_METHOD.FIELD_NAME;

export function PaymentMethod({ section, setSectionValues }: Props) {
  const electronicPaymentSelected = section.getFieldValue(ELECTRONIC_IS_ENABLED);
  const electronicServiceTypeFieldValue = section.getFieldValue(ELECTRONIC_SERVICE_TYPE);
  const { hasZillowPayments, otherElectronicPaymentOptions } = parseElectronicPaymentOptions(
    electronicServiceTypeFieldValue,
  );

  const [zillowPaymentsSelected, setZillowPaymentsSelected] = useState(hasZillowPayments);
  const [electronicPaymentMaskSelected, setElectronicPaymentMaskSelected] = useState(
    electronicPaymentSelected && electronicServiceTypeFieldValue !== ZILLOW_PAYMENTS,
  );

  const { setPreSubmitCallback } = usePreSubmitCallback();

  function paymentMethodCallbackFunction() {
    const electronicServiceTypeFieldValue = parseElectronicPaymentOptions(
      section.getFieldValue(ELECTRONIC_SERVICE_TYPE),
    ).otherElectronicPaymentOptions;

    if (zillowPaymentsSelected) {
      if (electronicServiceTypeFieldValue !== '') {
        setSectionValues({
          [ELECTRONIC_SERVICE_TYPE]: `${ZILLOW_PAYMENTS}, ${electronicServiceTypeFieldValue}`,
        });
      } else {
        setSectionValues({
          [ELECTRONIC_SERVICE_TYPE]: `${ZILLOW_PAYMENTS}`,
          [ELECTRONIC_IS_ENABLED]: true,
        });
      }
    } else {
      if (electronicPaymentMaskSelected !== section.getFieldValue(ELECTRONIC_IS_ENABLED)) {
        setSectionValues({
          [ELECTRONIC_IS_ENABLED]: false,
        });
      }
    }
  }

  useEffect(() => {
    if (hasZillowPayments) {
      setSectionValues({
        [ELECTRONIC_SERVICE_TYPE]: otherElectronicPaymentOptions,
      });
    }

    setPreSubmitCallback(paymentMethodCallbackFunction);

    return () => {
      // Clean up callback on unmount
      setPreSubmitCallback(null);
    };
  }, []);

  function onSelectZillowPayment() {
    setZillowPaymentsSelected(!zillowPaymentsSelected);
  }

  function onSelectElectronic() {
    const electronicField = electronicPaymentMaskSelected;

    setElectronicPaymentMaskSelected(!electronicPaymentMaskSelected);

    setSectionValues({
      [ELECTRONIC_IS_ENABLED]: !electronicField,
      [ELECTRONIC_SERVICE_TYPE]: '',
    });
  }

  function onSelectDirect() {
    setSectionValues({
      [DIRECT_IS_ENABLED]: !section.getFieldValue(DIRECT_IS_ENABLED),
    });
  }

  function onSelectCheck() {
    const checkField = section.getFieldValue(CHECK_IS_ENABLED);

    setSectionValues({
      [CHECK_IS_ENABLED]: !checkField,
      [CHECK_ADDRESS]: '',
    });
  }

  function onAddressChange(event: FormEvent<HTMLInputElement>) {
    setSectionValues({
      [CHECK_ADDRESS]: event.currentTarget.value,
    });
  }

  function onElectronicServiceTypeChange(event: FormEvent<HTMLInputElement>) {
    setSectionValues({
      [ELECTRONIC_SERVICE_TYPE]: event.currentTarget.value,
    });
  }

  return (
    <>
      <Spacer marginBottom="xs">
        <Text fontType="h6" fontColor="">
          Which forms of payment will you accept?
        </Text>
        <Text fontColor="textMedium"> (check all that apply)</Text>
      </Spacer>
      <SelectionGroup>
        <SelectionGroup.Item onClick={onSelectZillowPayment} isSelected={zillowPaymentsSelected}>
          <SelectionGroup.Header>
            <Paragraph fontType="bodyHeading" fontColor="">
              <IconZillowLogo fontColor={zillowPaymentsSelected ? '' : 'brand'} marginRight="xs" />
              Zillow Payments
            </Paragraph>
          </SelectionGroup.Header>
          <SelectionGroup.Content>
            <Paragraph fontType="bodySmall" fontColor="">
              Tenant pays through our online service. Set up quickly using information from your lease.
            </Paragraph>
          </SelectionGroup.Content>
        </SelectionGroup.Item>
        <SelectionGroup.Item onClick={onSelectElectronic} isSelected={electronicPaymentMaskSelected}>
          <SelectionGroup.Header>
            <Paragraph fontType="bodyHeading" fontColor="">
              Other electronic payment
            </Paragraph>
          </SelectionGroup.Header>
          <SelectionGroup.Content>
            <Paragraph fontType="bodySmall" fontColor="">
              Tenant pays via third-party online service of your choice.
            </Paragraph>
          </SelectionGroup.Content>
        </SelectionGroup.Item>
        <SelectionGroup.Item
          onClick={onSelectDirect}
          isSelected={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.DIRECT_IS_ENABLED)}
        >
          <SelectionGroup.Header>
            <Paragraph fontType="bodyHeading" fontColor="">
              Direct deposit
            </Paragraph>
          </SelectionGroup.Header>
          <SelectionGroup.Content>
            <Paragraph fontType="bodySmall" fontColor="">
              Tenant transfers funds using bank info, including ACH, if you provide them.
            </Paragraph>
          </SelectionGroup.Content>
        </SelectionGroup.Item>
        <SelectionGroup.Item
          onClick={onSelectCheck}
          isSelected={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED)}
        >
          <SelectionGroup.Header>
            <Paragraph fontType="bodyHeading" fontColor="">
              Check or money order
            </Paragraph>
          </SelectionGroup.Header>
          <SelectionGroup.Content>
            <Paragraph fontType="bodySmall" fontColor="">
              Tenant sends payment to either the landlord or the property manager.
            </Paragraph>
          </SelectionGroup.Content>
        </SelectionGroup.Item>
      </SelectionGroup>
      {electronicPaymentMaskSelected && (
        <FormField marginTop="sm">
          <Label id="payment-service">What electronic payment service would you like to use?</Label>
          <Input
            onChange={onElectronicServiceTypeChange}
            value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.ELECTRONIC_SERVICE_TYPE)}
            aria-labelledby="payment-service"
            type="text"
          />
        </FormField>
      )}
      {section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_IS_ENABLED) && (
        <FormField marginTop="sm">
          <Label id="whom-send-check-or-money-order">To whom should the tenant send check or money order?</Label>
          <Textarea
            error={
              section.hasFieldError(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)
                ? PAYMENT_METHOD.ERROR_MESSAGE.ADDRESS
                : null
            }
            onChange={onAddressChange}
            value={section.getFieldValue(PAYMENT_METHOD.FIELD_NAME.CHECK_ADDRESS)}
            rows="3"
            aria-labelledby="whom-send-check-or-money-order"
          />
          <FormHelp>Recipient’s name, street, city, state, and ZIP Code</FormHelp>
        </FormField>
      )}
    </>
  );
}

export default PaymentMethod;
